import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import Cropper from 'react-easy-crop';
import API_BASE_URL from '../config/api';

const PhotoUploadPage = () => {
  const { school } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Get submission data and photos from previous page
  const submissionData = location.state?.submissionData || {};
  const existingPhotos = location.state?.photos || [];
  
  const [photos, setPhotos] = useState(existingPhotos);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [schoolName, setSchoolName] = useState('');
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [templateImage, setTemplateImage] = useState(null);
  
  // Crop modal state
  const [cropOpen, setCropOpen] = useState(false);
  const [cropIndex, setCropIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Touch drag state
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [isDraggingTouch, setIsDraggingTouch] = useState(false);
  
  // Refs for photo items to attach non-passive event listeners
  const photoItemRefs = useRef([]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (school === 'streamer-university') {
      navigate('/streamer/media', { replace: true, state: location.state });
    }
  }, [school, navigate]);

  // Fetch school name from database
  useEffect(() => {
    const fetchSchoolName = async () => {
      if (!school) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/schools/${school}`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setSchoolName(result.data.school_name);
        } else {
          setSchoolName(school.toUpperCase());
        }
      } catch (error) {
        console.error('Error fetching school name:', error);
        setSchoolName(school.toUpperCase());
      }
    };

    fetchSchoolName();
  }, [school]);

  // Load template image from S3
  useEffect(() => {
    const loadTemplate = async () => {
      if (!school) return;
      
      try {
        // Request template URL from backend
        const response = await fetch(`${API_BASE_URL}/api/template/${school}`);
        const result = await response.json();
        
        if (result.status === 'success' && result.data.templateUrl) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            setTemplateImage(img);
          };
          img.onerror = () => {
            console.error('Failed to load template image');
          };
          img.src = result.data.templateUrl;
        }
      } catch (error) {
        console.error('Error loading template:', error);
      }
    };

    loadTemplate();
  }, [school]);

  // Generate cover preview with template when photo or name changes
  useEffect(() => {
    if (photos.length > 0 && templateImage && submissionData.name) {
      generateCoverPreview();
    }
  }, [photos, templateImage, submissionData.name]);

  // Attach touch move handlers
  useEffect(() => {
    const photoItems = photoItemRefs.current;
    
    const touchMoveHandler = (e) => {
      if (draggedIndex === null || !touchStartPos) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.y);
      
      // Start dragging if moved more than 10px
      if (deltaX > 10 || deltaY > 10) {
        setIsDraggingTouch(true);
      }
      
      // Find which element we're over
      const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
      if (elementAtPoint) {
        const photoItem = elementAtPoint.closest('.photo-item');
        if (photoItem) {
          const allPhotoItems = Array.from(document.querySelectorAll('.photo-item'));
          const overIndex = allPhotoItems.indexOf(photoItem);
          if (overIndex !== -1 && overIndex !== draggedIndex) {
            setDragOverIndex(overIndex);
          }
        }
      }
    };

    // Attach listeners to each photo item
    photoItems.forEach((item, index) => {
      if (item) {
        const handler = (e) => touchMoveHandler(e, index);
        item.addEventListener('touchmove', handler);
        item._touchMoveHandler = handler; // Store for cleanup
      }
    });

    // Cleanup
    return () => {
      photoItems.forEach((item) => {
        if (item && item._touchMoveHandler) {
          item.removeEventListener('touchmove', item._touchMoveHandler);
        }
      });
    };
  }, [photos.length, draggedIndex, touchStartPos]);

  const generateCoverPreview = async () => {
    if (!photos[0] || !templateImage || !submissionData.name) return;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 800;
      canvas.width = size;
      canvas.height = size;

      // Load the cover photo
      const coverImg = new Image();
      coverImg.onload = () => {
        // Draw the cover photo
        ctx.drawImage(coverImg, 0, 0, size, size);

        // Draw the template overlay
        ctx.drawImage(templateImage, 0, 0, size, size);

        // Add the first name text
        const firstName = submissionData.name.split(' ')[0];
        const instagramHandle = submissionData.instagram_handle;
        
        // Adjust positions based on whether we have an Instagram handle
        const nameY = instagramHandle ? size - 85 : size - 65;
        const handleY = size - 40;
        
        // Draw name
        ctx.font = '1000 50px Tahoma, Geneva, sans-serif';
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(firstName.toUpperCase(), size / 2, nameY);
        
        // Draw Instagram handle if provided
        if (instagramHandle) {
          const cleanHandle = instagramHandle.startsWith('@') ? instagramHandle : `@${instagramHandle}`;
          ctx.font = '700 25px Tahoma, Geneva, sans-serif';
          ctx.fillText(cleanHandle, size / 2, handleY);
        }

        // Convert canvas to data URL
        const previewUrl = canvas.toDataURL('image/jpeg', 0.95);
        setCoverPreviewUrl(previewUrl);
      };
      coverImg.src = photos[0].previewUrl;
    } catch (error) {
      console.error('Error generating cover preview:', error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Check total photo limit
    const remainingSlots = 10 - photos.length;
    if (files.length > remainingSlots) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Too Many Photos',
        detail: `You can only add ${remainingSlots} more photos. Maximum is 10 total.`,
        life: 3000
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const validFiles = [];
    const totalFiles = files.length;

    // Validate all files first
    for (const file of files) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Invalid File',
          detail: `${file.name} is not a supported image format.`,
          life: 3000
        });
        continue;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'File Too Large',
          detail: `${file.name} is larger than 10MB.`,
          life: 3000
        });
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      setUploading(false);
      return;
    }

    // Process all valid files
    const processedPhotos = [];
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      
      try {
        // Store original image for cropping
        const originalUrl = URL.createObjectURL(file);
        
        // Auto-crop to square using canvas
        const croppedBlob = await autoCropToSquare(file);
        
        // Create preview URL and add to photos
        const previewUrl = URL.createObjectURL(croppedBlob);
        const newPhoto = {
          blob: croppedBlob,
          previewUrl: previewUrl,
          originalUrl: originalUrl, // Keep original for manual cropping
          fileName: `photo-${Date.now()}-${i}.jpg`,
          uploaded: false
        };

        processedPhotos.push(newPhoto);
        
        // Update progress
        setUploadProgress(((i + 1) / validFiles.length) * 100);
        
      } catch (error) {
        console.error('Processing error for file:', file.name, error);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Processing Failed',
          detail: `Failed to process ${file.name}`,
          life: 3000
        });
      }
    }

    // Add all processed photos at once
    setPhotos(prev => [...prev, ...processedPhotos]);
    
    toastRef.current?.show({
      severity: 'success',
      summary: 'Photos Added',
      detail: `Successfully added ${processedPhotos.length} photo(s)`,
      life: 3000
    });

    setUploading(false);
    setUploadProgress(0);
    
    // Clear the input
    event.target.value = '';
  };

  // Auto-crop function to create square images
  const autoCropToSquare = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas to square dimensions
        const size = 800;
        canvas.width = size;
        canvas.height = size;
        
        // Calculate crop dimensions to make it square
        const minDimension = Math.min(img.width, img.height);
        const startX = (img.width - minDimension) / 2;
        const startY = (img.height - minDimension) / 2;
        
        // Draw the cropped image
        ctx.drawImage(
          img,
          startX, startY, minDimension, minDimension,
          0, 0, size, size
        );
        
        // Convert to blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.9);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Crop completion callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Helper function to create cropped image
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const size = 800;
        canvas.width = size;
        canvas.height = size;
        
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          size,
          size
        );
        
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            resolve({ blob, url });
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.9);
      };
      
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = imageSrc;
    });
  };

  // Open crop modal for a specific photo
  const openCropModal = (index) => {
    setCropIndex(index);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropOpen(true);
  };

  // Save cropped image
  const saveCroppedImage = async () => {
    if (cropIndex === null || !croppedAreaPixels) return;
    
    try {
      const photo = photos[cropIndex];
      const imageSource = photo.originalUrl || photo.previewUrl;
      const { blob, url } = await createCroppedImage(imageSource, croppedAreaPixels);
      
      // Revoke old preview URL to prevent memory leak (but keep originalUrl)
      URL.revokeObjectURL(photo.previewUrl);
      
      // Update the photo
      const newPhotos = [...photos];
      newPhotos[cropIndex] = {
        ...photo,
        blob: blob,
        previewUrl: url
        // Keep originalUrl for future crops
      };
      setPhotos(newPhotos);
      
      // Close modal
      setCropOpen(false);
      setCropIndex(null);
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Photo Cropped',
        detail: 'Your photo has been cropped successfully.',
        life: 3000
      });
    } catch (error) {
      console.error('Error cropping image:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Crop Failed',
        detail: 'Failed to crop the image. Please try again.',
        life: 3000
      });
    }
  };



  const clearAllPhotos = () => {
    // Revoke object URLs to prevent memory leaks
    photos.forEach(photo => {
      if (photo.previewUrl) {
        URL.revokeObjectURL(photo.previewUrl);
      }
      if (photo.originalUrl) {
        URL.revokeObjectURL(photo.originalUrl);
      }
    });
    setPhotos([]);
    
    toastRef.current?.show({
      severity: 'info',
      summary: 'Photos Cleared',
      detail: 'All photos have been removed.',
      life: 3000
    });
  };

  const removePhoto = (index) => {
    const photoToRemove = photos[index];
    if (photoToRemove.previewUrl) {
      URL.revokeObjectURL(photoToRemove.previewUrl);
    }
    if (photoToRemove.originalUrl) {
      URL.revokeObjectURL(photoToRemove.originalUrl);
    }
    
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the photos array
    const newPhotos = [...photos];
    const draggedPhoto = newPhotos[draggedIndex];
    
    // Remove from old position
    newPhotos.splice(draggedIndex, 1);
    
    // Insert at new position
    newPhotos.splice(dropIndex, 0, draggedPhoto);
    
    setPhotos(newPhotos);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // Show toast if the cover image changed
    if (draggedIndex !== 0 && dropIndex === 0) {
      toastRef.current?.show({
        severity: 'info',
        summary: 'Cover Photo Updated',
        detail: 'This photo is now your cover image.',
        life: 3000
      });
    } else if (draggedIndex === 0 && dropIndex !== 0) {
      toastRef.current?.show({
        severity: 'info',
        summary: 'Cover Photo Updated',
        detail: 'The first photo is now your cover image.',
        life: 3000
      });
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setDraggedIndex(index);
    setIsDraggingTouch(false);
  };

  // Note: Touch move is handled via useEffect with non-passive listener

  const handleTouchEnd = (e) => {
    if (draggedIndex === null || !isDraggingTouch) {
      // If we didn't actually drag, reset and allow normal click
      setDraggedIndex(null);
      setDragOverIndex(null);
      setTouchStartPos(null);
      setIsDraggingTouch(false);
      return;
    }
    
    e.preventDefault();
    
    if (dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      // Reorder the photos array
      const newPhotos = [...photos];
      const draggedPhoto = newPhotos[draggedIndex];
      
      // Remove from old position
      newPhotos.splice(draggedIndex, 1);
      
      // Insert at new position
      newPhotos.splice(dragOverIndex, 0, draggedPhoto);
      
      setPhotos(newPhotos);
      
      // Show toast if the cover image changed
      if (draggedIndex !== 0 && dragOverIndex === 0) {
        toastRef.current?.show({
          severity: 'info',
          summary: 'Cover Photo Updated',
          detail: 'This photo is now your cover image.',
          life: 3000
        });
      } else if (draggedIndex === 0 && dragOverIndex !== 0) {
        toastRef.current?.show({
          severity: 'info',
          summary: 'Cover Photo Updated',
          detail: 'The first photo is now your cover image.',
          life: 3000
        });
      }
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTouchStartPos(null);
    setIsDraggingTouch(false);
  };

  const handleNext = async () => {
    // Guard against multiple simultaneous submissions
    if (uploading) {
      console.log('⚠️ Upload already in progress, ignoring duplicate submission attempt');
      return;
    }
    
    if (photos.length < 3) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'More Photos Needed',
        detail: 'Please upload at least 3 photos to continue.',
        life: 3000
      });
      return;
    }

    console.log('🚀 Starting submission process...');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Create profile submission
      setUploadProgress(10);
      console.log('📝 Creating submission with data:', { ...submissionData, school });
      
      const submissionPayload = {
        ...submissionData,
        school: school
      };

      const profileResponse = await fetch(`${API_BASE_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionPayload),
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to create submission');
      }

      const profileResult = await profileResponse.json();
      const submissionId = profileResult.data.id;

      // Step 2: Upload photos
      setUploadProgress(30);
      const photoKeys = [];
      const totalPhotos = photos.length;

      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        
        // Get signed URL from backend
        const signedUrlResponse = await fetch(`${API_BASE_URL}/api/photos/upload-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileName: photo.fileName,
            fileType: 'image/jpeg',
            submissionId: submissionId,
            school: school,
            instagramHandle: submissionData.instagram_handle || 'nohandle',
            photoIndex: i
          }),
        });

        if (!signedUrlResponse.ok) {
          throw new Error(`Failed to get upload URL for photo ${i + 1}`);
        }

        const { data } = await signedUrlResponse.json();
        
        // Create file from blob
        const file = new File([photo.blob], photo.fileName, { type: 'image/jpeg' });
        
        // Upload to S3
        const uploadResponse = await fetch(data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': 'image/jpeg',
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload photo ${i + 1} to S3`);
        }

        photoKeys.push(data.fileKey);
        
        // Update progress
        const photoProgress = ((i + 1) / totalPhotos) * 60;
        setUploadProgress(30 + photoProgress);
      }

      // Step 3: Update submission with photo keys
      setUploadProgress(90);
      console.log('Updating submission with photo keys:', photoKeys);
      
      const updateResponse = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photoKeys: photoKeys
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to update submission:', errorData);
        throw new Error(errorData.message || 'Failed to update submission with photos');
      }

      const updateResult = await updateResponse.json();
      console.log('Submission updated successfully:', updateResult);
      
      setUploadProgress(100);

      // Step 4: Navigate to tier selection
      toastRef.current?.show({
        severity: 'success',
        summary: 'Upload Complete',
        detail: 'Choose your posting speed...',
        life: 2000
      });

      setTimeout(() => {
        navigate(`/submit/${school}/tier-selection`, {
          state: {
            submissionId: submissionId,
            submissionData: submissionData,
            photos: photos
          }
        });
      }, 1000);

    } catch (error) {
      console.error('Submission error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.message || 'Failed to submit. Please try again.',
        life: 5000
      });
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBack = () => {
    if (photos.length > 0) {
      confirmDialog({
        message: 'Going back will remove all your uploaded photos. Are you sure you want to continue?',
        header: 'Warning',
        icon: '',
        accept: () => {
          // Clean up photo URLs
          photos.forEach(photo => {
            if (photo.previewUrl) {
              URL.revokeObjectURL(photo.previewUrl);
            }
            if (photo.originalUrl) {
              URL.revokeObjectURL(photo.originalUrl);
            }
          });
          navigate(`/submit/${school}`, { state: { submissionData } });
        },
        reject: () => {
          // Do nothing, stay on the page
        },
        acceptClassName: 'p-button-danger',
        acceptLabel: '',
        rejectLabel: ''
      });
    } else {
      navigate(`/submit/${school}`, { state: { submissionData } });
    }
  };

  return (
    <div className="photo-upload-page">
      <Toast ref={toastRef} />
      <ConfirmDialog />
      
      {/* Header */}
      <header className="form-header">
        <div className="header-content">
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="back-button"
            onClick={handleBack}
          />
          <div className="header-title">
            <p>{schoolName || 'Your University'}</p>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="progress-section">
        <div className="container">
          <div className="progress-info">
            <span>Step 2 of 3: Photos</span>
            <span>{photos.length}/10 photos • {photos.length >= 3 ? 'Ready' : `Need ${3 - photos.length} more`}</span>
          </div>
          <ProgressBar 
            value={Math.min((photos.length / 3) * 100, 100)} 
            className="form-progress"
            showValue={false}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="photo-upload-main">
        <div className="container">
          
          {/* Cover Photo Preview */}
          {photos.length > 0 && (
            <Card className="cover-preview-card">
              <div className="cover-preview-header">
                <h2>Cover Photo Preview</h2>
                <p>This is how your cover photo will appear on Instagram with the template</p>
              </div>
              
              <div className="instagram-post-preview">
                <div className="post-image-container">
                  {coverPreviewUrl ? (
                    <img 
                      src={coverPreviewUrl} 
                      alt="Cover with template" 
                      className="cover-image"
                    />
                  ) : (
                    <div className="loading-preview">
                      <img 
                        src={photos[0].previewUrl} 
                        alt="Cover" 
                        className="cover-image"
                      />
                      <div className="loading-overlay">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading template...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Upload Section */}
          <Card className="upload-section-card">
            <div className="upload-controls">
              <h2>Upload Photos</h2>
              <p>Upload 3-10 photos. Drag photos to reorder them.</p>
              
              <div className="upload-buttons">
                                  <Button
                    icon="pi pi-camera"
                    label={photos.length === 0 ? "Upload Photos" : "Add More Photos"}
                    className="upload-btn"
                    onClick={handleUploadClick}
                    disabled={photos.length >= 10 || uploading}
                  />
                
                {photos.length > 0 && (
                                      <Button
                      icon="pi pi-trash"
                      label="Clear All"
                      className="clear-btn p-button-outlined"
                      onClick={clearAllPhotos}
                      disabled={uploading}
                    />
                )}
                              </div>

                {uploading && (
                  <div className="upload-status">
                    <p>Processing {Math.round(uploadProgress)}% complete...</p>
                    <ProgressBar value={uploadProgress} showValue={false} />
                  </div>
                )}
              </div>

              {/* Photo Grid */}
            {photos.length > 0 && (
              <div className="photo-grid">
                {photos.map((photo, index) => (
                  <div 
                    key={index}
                    ref={(el) => (photoItemRefs.current[index] = el)}
                    className={`photo-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, index)}
                    onTouchEnd={handleTouchEnd}
                    style={{ cursor: 'grab' }}
                  >
                    <div className="photo-wrapper">
                      <img src={photo.previewUrl} alt={`Photo ${index + 1}`} />
                      {index === 0 && (
                        <div className="cover-badge">Cover</div>
                      )}
                      <div className="drag-handle">
                        <i className="pi pi-arrows-alt"></i>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', gap: '8px' }}>
                      <Button
                        icon="pi pi-expand"
                        className="p-button-rounded p-button-text"
                        onClick={() => openCropModal(index)}
                        title="Crop photo"
                        style={{ color: '#000000', width: '36px', height: '36px' }}
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-text"
                        onClick={() => removePhoto(index)}
                        title="Remove photo"
                        style={{ color: '#dc3545', width: '36px', height: '36px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Next Button */}
            <div className="form-actions">
                              <Button
                  label={uploading ? "Uploading..." : "Next: Select Tier"}
                  icon={uploading ? "pi pi-spin pi-spinner" : "pi pi-arrow-right"}
                  iconPos="right"
                  className="next-button"
                  onClick={handleNext}
                  disabled={photos.length < 3 || uploading}
                />
            </div>
          </Card>
        </div>
      </main>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Crop Modal */}
      {cropOpen && cropIndex !== null && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Crop Area */}
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Cropper
              image={photos[cropIndex].originalUrl || photos[cropIndex].previewUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          {/* Controls */}
          <div 
            style={{
              padding: '20px',
              backgroundColor: '#000000',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}
          >
            {/* Zoom Label */}
            <div style={{ textAlign: 'center', color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>
              Pinch or drag to zoom and position
            </div>

            {/* Zoom Slider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <i className="pi pi-search-minus" style={{ color: '#ffffff', fontSize: '18px' }}></i>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ 
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  outline: 'none',
                  background: '#4a4a4a'
                }}
              />
              <i className="pi pi-search-plus" style={{ color: '#ffffff', fontSize: '18px' }}></i>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                label="Cancel"
                className="p-button-secondary"
                onClick={() => {
                  setCropOpen(false);
                  setCropIndex(null);
                }}
                style={{ color: '#ffffff', flex: 1, fontSize: '16px', padding: '12px' }}
              />
              <Button
                label="Save"
                className="p-button-success"
                onClick={saveCroppedImage}
                style={{ color: '#ffffff', flex: 1, fontSize: '16px', padding: '12px' }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};



export default PhotoUploadPage; 